/**
 * Board geometry and bitmask helpers shared by the solver, the human
 * technique engine, the generator and the game-state model.
 *
 * A grid is a flat array of 81 cells (Int8Array), values 0..9 where 0 means
 * empty. Cell index i maps to row = i/9, col = i%9, box = (row/3)*3 + col/3.
 *
 * Candidate sets are stored as 9-bit masks: bit (d-1) set means digit d is a
 * candidate. This keeps the technique engine fast and allocation-free.
 */

export const SIZE = 9;
export const BOX = 3;
export const CELL_COUNT = 81;

/** Mask with all nine digits present (0b1_1111_1111). */
export const ALL_DIGITS_MASK = 0b111111111;

export type Grid = Int8Array;

export const rowOf = (i: number): number => (i / 9) | 0;
export const colOf = (i: number): number => i % 9;
export const boxOf = (i: number): number =>
  (((((i / 9) | 0) / 3) | 0) * 3) + (((i % 9) / 3) | 0);

// ---------------------------------------------------------------------------
// Units (the 27 houses: 9 rows, 9 columns, 9 boxes) and peer relationships.
// All precomputed once at module load.
// ---------------------------------------------------------------------------

function buildUnits(): number[][] {
  const rows: number[][] = Array.from({ length: SIZE }, () => []);
  const cols: number[][] = Array.from({ length: SIZE }, () => []);
  const boxes: number[][] = Array.from({ length: SIZE }, () => []);
  for (let i = 0; i < CELL_COUNT; i++) {
    rows[rowOf(i)].push(i);
    cols[colOf(i)].push(i);
    boxes[boxOf(i)].push(i);
  }
  return [...rows, ...cols, ...boxes];
}

/** The 27 units. Indices 0..8 rows, 9..17 columns, 18..26 boxes. */
export const UNITS: readonly number[][] = buildUnits();

export const ROW_UNITS = UNITS.slice(0, 9);
export const COL_UNITS = UNITS.slice(9, 18);
export const BOX_UNITS = UNITS.slice(18, 27);

/** For each cell, the indices into UNITS of its [row, col, box] units. */
export const UNIT_INDICES_OF_CELL: readonly number[][] = (() => {
  const out: number[][] = Array.from({ length: CELL_COUNT }, () => []);
  for (let u = 0; u < UNITS.length; u++) {
    for (const cell of UNITS[u]) out[cell].push(u);
  }
  return out;
})();

/** For each cell, the 20 peer cells sharing a row, column or box. */
export const PEERS: readonly number[][] = (() => {
  const out: number[][] = [];
  for (let i = 0; i < CELL_COUNT; i++) {
    const set = new Set<number>();
    for (const u of UNIT_INDICES_OF_CELL[i]) {
      for (const cell of UNITS[u]) if (cell !== i) set.add(cell);
    }
    out.push([...set].sort((a, b) => a - b));
  }
  return out;
})();

// ---------------------------------------------------------------------------
// Bitmask helpers
// ---------------------------------------------------------------------------

/** Mask for a single digit 1..9. */
export const maskOf = (digit: number): number => 1 << (digit - 1);

export const hasDigit = (mask: number, digit: number): boolean =>
  (mask & (1 << (digit - 1))) !== 0;

/** Population count of a 9-bit mask. */
export function popcount(mask: number): number {
  let m = mask & ALL_DIGITS_MASK;
  let count = 0;
  while (m) {
    m &= m - 1;
    count++;
  }
  return count;
}

/** The digits (1..9) present in a mask, ascending. */
export function digitsOf(mask: number): number[] {
  const out: number[] = [];
  for (let d = 1; d <= SIZE; d++) if (mask & (1 << (d - 1))) out.push(d);
  return out;
}

/** Lowest digit present, or 0 if the mask is empty. */
export function firstDigit(mask: number): number {
  for (let d = 1; d <= SIZE; d++) if (mask & (1 << (d - 1))) return d;
  return 0;
}

// ---------------------------------------------------------------------------
// Parsing / serialization
// ---------------------------------------------------------------------------

/**
 * Parse an 81-character puzzle string. Accepts '.', '0' or ' ' for empty and
 * '1'..'9' for clues. Whitespace and common separators are ignored so grids
 * pasted from other sources still load.
 */
export function parseGrid(input: string): Grid {
  const cleaned = input.replace(/[\r\n\t |_]/g, "");
  if (cleaned.length !== CELL_COUNT) {
    throw new Error(
      `Expected 81 cells but got ${cleaned.length} after cleaning input.`,
    );
  }
  const grid = new Int8Array(CELL_COUNT);
  for (let i = 0; i < CELL_COUNT; i++) {
    const ch = cleaned[i];
    if (ch === "." || ch === "0") grid[i] = 0;
    else if (ch >= "1" && ch <= "9") grid[i] = ch.charCodeAt(0) - 48;
    else throw new Error(`Invalid character '${ch}' at position ${i}.`);
  }
  return grid;
}

/** Serialize a grid to an 81-char string using '.' for empties. */
export function gridToString(grid: Grid, empty = "."): string {
  let out = "";
  for (let i = 0; i < CELL_COUNT; i++) out += grid[i] === 0 ? empty : String(grid[i]);
  return out;
}

export function cloneGrid(grid: Grid): Grid {
  return grid.slice() as Grid;
}

/** True if no unit contains a duplicate non-zero digit. */
export function isConsistent(grid: Grid): boolean {
  for (const unit of UNITS) {
    let seen = 0;
    for (const cell of unit) {
      const v = grid[cell];
      if (v === 0) continue;
      const bit = 1 << (v - 1);
      if (seen & bit) return false;
      seen |= bit;
    }
  }
  return true;
}

/** True if the grid is completely filled and valid. */
export function isComplete(grid: Grid): boolean {
  for (let i = 0; i < CELL_COUNT; i++) if (grid[i] === 0) return false;
  return isConsistent(grid);
}

/**
 * Candidate mask for an empty cell given the current grid: all digits minus
 * those already used by any peer.
 */
export function candidatesForCell(grid: Grid, cell: number): number {
  if (grid[cell] !== 0) return 0;
  let used = 0;
  for (const p of PEERS[cell]) {
    const v = grid[p];
    if (v !== 0) used |= 1 << (v - 1);
  }
  return ALL_DIGITS_MASK & ~used;
}
